
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        form.reset();
        navigate('/thanks');
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      alert('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ナビゲーション */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://static.readdy.ai/image/4b210e4d3d9fe9a73b886d10fa116566/0e64fe7d7ddf660e67742842ccecd709.png" alt="ロゴ" className="h-8 md:h-10 w-auto" />
          </div>
          <a href="#contact" className="bg-[#2C5F2D] text-white px-5 md:px-8 py-2 md:py-3 rounded-full font-bold text-sm md:text-base hover:bg-[#234a23] transition-colors whitespace-nowrap cursor-pointer">
            無料診断を申し込む
          </a>
        </div>
      </nav>

      {/* ヒーローセクション */}
      <section className="relative pt-20 md:pt-32 pb-12 md:pb-24 px-4 md:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F0F7F0]/30 to-white"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl lg:text-[90px] leading-[1.1] font-black mb-4 md:mb-8">
                外構工事の<br />
                <span className="text-[#2C5F2D]">「粗利」</span>を<br />
                増やす設計。
              </h1>
              <p className="text-base md:text-xl lg:text-2xl text-[#666666] mb-3 md:mb-6 leading-relaxed">
                問い合わせ数ではなく、<strong>成約率と単価</strong>を動かす。
              </p>
              <p className="text-base md:text-xl lg:text-2xl text-[#666666] mb-6 md:mb-12 leading-relaxed">
                価格競争から抜ける<strong>「利益改善コンサル」</strong>。
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-6 mb-4 md:mb-6">
                <a href="#contact" className="bg-[#2C5F2D] text-white px-6 md:px-10 py-3 md:py-4 rounded-2xl font-bold text-sm md:text-lg hover:bg-[#234a23] transition-all hover:shadow-lg flex items-center justify-center gap-2 md:gap-3 whitespace-nowrap cursor-pointer">
                  無料診断で粗利改善シミュレーションを受ける
                  <i className="ri-arrow-right-line text-lg md:text-xl"></i>
                </a>
                <a href="#results" className="bg-white text-[#2C5F2D] border-2 border-[#2C5F2D] px-6 md:px-10 py-3 md:py-4 rounded-2xl font-bold text-sm md:text-lg hover:bg-[#F0F7F0] transition-all whitespace-nowrap cursor-pointer text-center">
                  改善事例を見る
                </a>
              </div>
              <p className="text-xs md:text-sm text-[#999999]">※効果は各社の数値により異なります。</p>
            </div>
            <div className="relative mt-6 lg:mt-0">
              <img 
                src="https://public.readdy.ai/ai/img_res/97538f9a853e957b358f41905900d45e.jpg" 
                alt="外構施工例" 
                className="w-full h-[350px] md:h-[500px] lg:h-[600px] object-cover rounded-2xl md:rounded-3xl shadow-xl md:shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 問題提起セクション */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl lg:text-6xl font-black mb-8 md:mb-16">
            問い合わせが増えても、<br />
            利益は増えていますか？
          </h2>

          <div className="bg-[#F8F8F8] rounded-2xl md:rounded-3xl p-6 md:p-12 mb-8 md:mb-12 text-left">
            <div className="space-y-4 md:space-y-6 mb-6 md:mb-8">
              {[
                '相見積もりで値引きが常態化',
                '成約率が25％前後で横ばい',
                '単価が上がらない',
                '見積もり提出後の失注が多い'
              ].map((text, index) => (
                <div key={index} className="flex items-start gap-3 md:gap-4 text-base md:text-lg lg:text-xl">
                  <div className="w-2 h-2 bg-[#2C5F2D] rounded-full flex-shrink-0 mt-2"></div>
                  <span className="text-[#666666]">{text}</span>
                </div>
              ))}
            </div>
            <p className="text-base md:text-xl lg:text-2xl text-[#666666] leading-relaxed">
              その原因は、広告ではなく<strong>「設計」</strong>にあります。
            </p>
          </div>

          <div className="bg-[#2C5F2D] text-white py-5 md:py-8 px-6 md:px-10 rounded-2xl md:rounded-3xl inline-block">
            <p className="text-lg md:text-2xl lg:text-3xl font-black">
              私たちは"問い合わせ増"ではなく<br className="hidden md:block" />
              <span className="text-[#7CB342]">"粗利増"</span>を設計します。
            </p>
          </div>
        </div>
      </section>

      {/* ROI可視化セクション */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-[#F0F7F0]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl md:text-3xl lg:text-5xl font-black text-center mb-2 md:mb-4">
            御社の数値で、取りこぼしを可視化します（目安）
          </h2>
          <p className="text-sm md:text-lg text-center text-[#666666] mb-8 md:mb-16">
            平均ではなく、御社の単価・粗利・成約率で算出します。
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-12">
            <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg">
              <div className="inline-block bg-[#E0E0E0] text-[#666666] px-4 md:px-6 py-1 md:py-2 rounded-full font-bold text-xs md:text-sm mb-4 md:mb-6">
                現状（例）
              </div>
              <div className="space-y-3 md:space-y-4 text-sm md:text-base">
                <div className="flex justify-between items-center">
                  <span className="text-[#666666]">月問い合わせ</span>
                  <span className="font-bold text-lg md:text-2xl">4件</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#666666]">成約率</span>
                  <span className="font-bold text-lg md:text-2xl">25%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#666666]">月成約</span>
                  <span className="font-bold text-lg md:text-2xl">1件</span>
                </div>
                <div className="flex justify-between items-center pt-3 md:pt-4 border-t-2 border-[#E0E0E0]">
                  <span className="text-[#666666]">平均粗利</span>
                  <span className="font-bold text-xl md:text-3xl text-[#2C5F2D]">60万円</span>
                </div>
              </div>
            </div>

            <div className="bg-[#2C5F2D] text-white rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg">
              <div className="inline-block bg-[#7CB342] text-[#2C5F2D] px-4 md:px-6 py-1 md:py-2 rounded-full font-bold text-xs md:text-sm mb-4 md:mb-6">
                改善後（例）
              </div>
              <div className="space-y-3 md:space-y-4 text-sm md:text-base">
                <div className="flex justify-between items-center">
                  <span className="opacity-90">問い合わせ</span>
                  <span className="font-bold text-lg md:text-2xl">6件</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="opacity-90">成約率</span>
                  <span className="font-bold text-lg md:text-2xl">30%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="opacity-90">月成約</span>
                  <span className="font-bold text-lg md:text-2xl">1.8件（目安）</span>
                </div>
                <div className="flex justify-between items-center pt-3 md:pt-4 border-t-2 border-white/20">
                  <span className="opacity-90">平均粗利</span>
                  <span className="font-bold text-xl md:text-3xl text-[#7CB342]">60万円</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-10 text-center shadow-lg mb-4 md:mb-6">
            <p className="text-base md:text-xl text-[#666666] mb-3 md:mb-4">
              増加分：<strong className="text-[#2C5F2D]">0.8件</strong> × 60万円 ＝
            </p>
            <p className="text-3xl md:text-5xl lg:text-6xl font-black text-[#2C5F2D] mb-3 md:mb-4">
              +48万円 / 月（目安）
            </p>
            <p className="text-xl md:text-3xl font-bold text-[#7CB342]">
              年間：+576万円（目安）
            </p>
          </div>

          <p className="text-xs md:text-sm text-center text-[#666666] mb-6 md:mb-8">
            ※単価・粗利・対応体制により変動します。無料診断で再計算します。
          </p>

          <div className="text-center">
            <a href="#contact" className="inline-flex items-center gap-2 md:gap-3 bg-[#2C5F2D] text-white px-6 md:px-10 py-3 md:py-4 rounded-2xl font-bold text-sm md:text-lg hover:bg-[#234a23] transition-all whitespace-nowrap cursor-pointer">
              御社の回収ラインを算出する
              <i className="ri-arrow-right-line text-lg md:text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* 4レバーセクション */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-3xl lg:text-5xl font-black text-center mb-8 md:mb-16">外構の利益は、4つのレバーで動く。</h2>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-12">
            {[
              { num: '1', title: '問い合わせ数', sub: '地域検索設計' },
              { num: '2', title: '成約率', sub: '導線・見積もり構造' },
              { num: '3', title: '単価', sub: '強み言語化・提案設計' },
              { num: '4', title: '失注率', sub: 'フォロー設計' }
            ].map((item, index) => (
              <div key={index} className="bg-white border-2 border-[#E0E0E0] rounded-xl md:rounded-2xl p-4 md:p-8 hover:border-[#2C5F2D] transition-colors text-center">
                <div className="w-10 h-10 md:w-16 md:h-16 bg-[#2C5F2D] rounded-full flex items-center justify-center mx-auto mb-3 md:mb-6">
                  <span className="text-xl md:text-3xl font-black text-white">{item.num}</span>
                </div>
                <h3 className="text-sm md:text-xl lg:text-2xl font-black mb-1 md:mb-3">{item.title}</h3>
                <p className="text-xs md:text-base text-[#666666]">{item.sub}</p>
              </div>
            ))}
          </div>

          <p className="text-base md:text-2xl lg:text-3xl font-bold text-[#2C5F2D] text-center">
            広告だけでは動かない指標を、構造で改善します。
          </p>
        </div>
      </section>

      {/* サービス説明セクション */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-[#F8F8F8]">
        <div className="max-w-4xl mx-auto text-center mb-8 md:mb-16">
          <h2 className="text-xl md:text-3xl lg:text-5xl font-black mb-6 md:mb-12">制作ではなく、利益構造の再設計。</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-6 text-left max-w-3xl mx-auto mb-8 md:mb-12">
            {[
              '競合比較と価格ポジション再定義',
              '施工写真の"売れる"再構造化',
              '見積もり導線の再設計',
              '失注分析と改善ロードマップ',
              '広告費の最適化'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 md:gap-4 text-sm md:text-lg lg:text-xl">
                <div className="w-5 h-5 md:w-6 md:h-6 bg-[#2C5F2D] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-white text-xs md:text-sm"></i>
                </div>
                <span className="font-bold">{item}</span>
              </div>
            ))}
          </div>

          <div className="bg-[#2C5F2D] text-white py-4 md:py-6 px-6 md:px-10 rounded-xl md:rounded-2xl inline-block">
            <p className="text-base md:text-xl lg:text-2xl font-black">値下げせずに成約率を上げる設計。</p>
          </div>
        </div>
      </section>

      {/* 料金セクション */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl md:text-3xl lg:text-5xl font-black text-center mb-2 md:mb-4">投資回収を前提とした料金設計</h2>
          <p className="text-sm md:text-lg lg:text-xl text-center text-[#666666] mb-8 md:mb-16">
            本プランは制作費ではなく、粗利改善の投資です。<br />
            月1件の成約増で回収可能な水準を目安に設計しています。
          </p>
          
          <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-12">
            <div className="bg-white border-2 border-[#E0E0E0] rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg hover:border-[#2C5F2D] transition-colors">
              <h3 className="text-lg md:text-2xl font-bold mb-4 md:mb-6">設計実装プラン</h3>
              <p className="text-3xl md:text-5xl font-black mb-4 md:mb-6 text-[#2C5F2D]">55万円〜</p>
              <ul className="space-y-3 md:space-y-4 text-sm md:text-base text-[#666666] mb-4 md:mb-6">
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-check-line text-[#2C5F2D] mt-0.5 md:mt-1"></i>
                  <span>設計書の実装</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-check-line text-[#2C5F2D] mt-0.5 md:mt-1"></i>
                  <span>LP制作・改修</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-check-line text-[#2C5F2D] mt-0.5 md:mt-1"></i>
                  <span>広告設定サポート</span>
                </li>
              </ul>
              <div className="bg-[#F0F7F0] rounded-lg md:rounded-xl p-3 md:p-4">
                <p className="text-xs md:text-sm text-[#2C5F2D] font-bold">
                  目安：平均粗利60万円の場合、月1件増で回収可能
                </p>
              </div>
            </div>

            <div className="bg-white border-2 border-[#E0E0E0] rounded-2xl md:rounded-3xl p-6 md:p-10 shadow-lg hover:border-[#2C5F2D] transition-colors">
              <h3 className="text-lg md:text-2xl font-bold mb-4 md:mb-6">伴走改善プラン</h3>
              <p className="text-3xl md:text-5xl font-black mb-4 md:mb-6 text-[#2C5F2D]">月20万円〜</p>
              <ul className="space-y-3 md:space-y-4 text-sm md:text-base text-[#666666] mb-4 md:mb-6">
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-check-line text-[#2C5F2D] mt-0.5 md:mt-1"></i>
                  <span>継続的な改善支援</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-check-line text-[#2C5F2D] mt-0.5 md:mt-1"></i>
                  <span>月次レポート</span>
                </li>
                <li className="flex items-start gap-2 md:gap-3">
                  <i className="ri-check-line text-[#2C5F2D] mt-0.5 md:mt-1"></i>
                  <span>施策の優先順位提案</span>
                </li>
              </ul>
              <div className="bg-[#F0F7F0] rounded-lg md:rounded-xl p-3 md:p-4">
                <p className="text-xs md:text-sm text-[#2C5F2D] font-bold">
                  目安：平均粗利60万円の場合、月1件増で回収可能
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 実績セクション */}
      <section id="results" className="py-12 md:py-24 px-4 md:px-6 bg-[#F0F7F0]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-3xl lg:text-5xl font-black text-center mb-8 md:mb-16">改善実績</h2>
          
          <div className="grid md:grid-cols-2 gap-4 md:gap-16 items-center mb-8 md:mb-16">
            <div>
              <div className="bg-[#F8F8F8] rounded-2xl md:rounded-3xl p-3 md:p-4">
                <p className="text-xs md:text-sm font-bold text-[#666666] mb-2 md:mb-4 px-2 md:px-4">Before</p>
                <img 
                  src="https://static.readdy.ai/image/4b210e4d3d9fe9a73b886d10fa116566/fa49089f66db6a2935f1b3a7367e1fe7.jpeg" 
                  alt="改善前" 
                  className="w-full h-[180px] md:h-[300px] object-cover rounded-xl md:rounded-2xl grayscale"
                />
              </div>
            </div>
            <div>
              <div className="bg-white rounded-2xl md:rounded-3xl p-3 md:p-4">
                <p className="text-xs md:text-sm font-bold text-[#2C5F2D] mb-2 md:mb-4 px-2 md:px-4">After</p>
                <img 
                  src="https://static.readdy.ai/image/4b210e4d3d9fe9a73b886d10fa116566/455dac1ecfc1ae7057087152356d9682.jpeg" 
                  alt="改善後" 
                  className="w-full h-[180px] md:h-[300px] object-cover rounded-xl md:rounded-2xl"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-10">
            <h3 className="text-lg md:text-2xl font-bold text-center mb-4 md:mb-8">問い合わせ数の推移</h3>
            <div className="relative h-48 md:h-64 overflow-x-auto">
              <svg className="w-full h-full min-w-[500px]" viewBox="0 0 800 250" preserveAspectRatio="xMidYMid meet">
                <line x1="50" y1="200" x2="750" y2="200" stroke="#E0E0E0" strokeWidth="2"/>
                <line x1="50" y1="50" x2="50" y2="200" stroke="#E0E0E0" strokeWidth="2"/>
                
                <text x="30" y="210" className="text-xs fill-[#666666]">0</text>
                <text x="20" y="160" className="text-xs fill-[#666666]">5</text>
                <text x="15" y="110" className="text-xs fill-[#666666]">10</text>
                <text x="15" y="60" className="text-xs fill-[#666666]">15</text>
                
                <polyline
                  points="100,180 200,175 300,170 400,140 500,110 600,80 700,60"
                  fill="none"
                  stroke="#2C5F2D"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                
                {[
                  { x: 100, y: 180, label: '1月' },
                  { x: 200, y: 175, label: '2月' },
                  { x: 300, y: 170, label: '3月' },
                  { x: 400, y: 140, label: '4月' },
                  { x: 500, y: 110, label: '5月' },
                  { x: 600, y: 80, label: '6月' },
                  { x: 700, y: 60, label: '7月' }
                ].map((point, i) => (
                  <g key={i}>
                    <circle cx={point.x} cy={point.y} r="6" fill="#2C5F2D"/>
                    <text x={point.x} y="230" textAnchor="middle" className="text-xs fill-[#666666]">{point.label}</text>
                  </g>
                ))}
              </svg>
            </div>
            <p className="text-center text-sm md:text-base text-[#666666] mt-4 md:mt-8">
              <span className="font-bold text-[#2C5F2D]">月3件 → 月12件</span>に改善（4ヶ月で4倍）
            </p>
          </div>
        </div>
      </section>

      {/* CTAセクション */}
      <section id="contact" className="py-12 md:py-24 px-4 md:px-6 bg-[#2C5F2D]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-black mb-4 md:mb-6">
            まずは御社の<br />
            <span className="text-[#7CB342]">"粗利回収ライン"</span>を算出します。
          </h2>
          <p className="text-sm md:text-lg lg:text-xl mb-8 md:mb-12 opacity-90">
            以下3項目をご共有ください。<br />
            改善余地と回収期間を提示します。
          </p>
          
          <form 
            id="contact-form"
            data-readdy-form
            action="https://readdy.ai/api/form/d67etg5bd17lru29kec0"
            method="POST"
            className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-10 text-left max-w-2xl mx-auto"
            onSubmit={handleSubmit}
          >
            <div className="bg-[#F0F7F0] rounded-xl md:rounded-2xl p-4 md:p-6 mb-6 md:mb-8">
              <p className="text-sm md:text-base text-[#2C5F2D] font-bold text-center">
                <i className="ri-calculator-line mr-2"></i>
                粗利改善シミュレーションを無料で実施
              </p>
            </div>
            
            <div className="space-y-4 md:space-y-6">
              <div>
                <label htmlFor="company" className="block text-xs md:text-sm font-bold text-[#1A1A1A] mb-1 md:mb-2">
                  会社名 <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text" 
                  id="company"
                  name="company"
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-[#E0E0E0] rounded-lg md:rounded-xl focus:border-[#2C5F2D] focus:outline-none text-sm text-[#1A1A1A]"
                  placeholder="株式会社〇〇"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-xs md:text-sm font-bold text-[#1A1A1A] mb-1 md:mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text" 
                  id="name"
                  name="name"
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-[#E0E0E0] rounded-lg md:rounded-xl focus:border-[#2C5F2D] focus:outline-none text-sm text-[#1A1A1A]"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-xs md:text-sm font-bold text-[#1A1A1A] mb-1 md:mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input 
                  type="email" 
                  id="email"
                  name="email"
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-[#E0E0E0] rounded-lg md:rounded-xl focus:border-[#2C5F2D] focus:outline-none text-sm text-[#1A1A1A]"
                  placeholder="example@company.co.jp"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs md:text-sm font-bold text-[#1A1A1A] mb-1 md:mb-2">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input 
                  type="tel" 
                  id="phone"
                  name="phone"
                  required
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-[#E0E0E0] rounded-lg md:rounded-xl focus:border-[#2C5F2D] focus:outline-none text-sm text-[#1A1A1A]"
                  placeholder="03-1234-5678"
                />
              </div>

              <div className="bg-[#F0F7F0] rounded-lg md:rounded-xl p-4 md:p-6 space-y-3 md:space-y-4">
                <p className="text-xs md:text-sm font-bold text-[#2C5F2D]">粗利算出に必要な情報</p>
                
                <div>
                  <label htmlFor="average_price" className="block text-xs md:text-sm font-bold text-[#1A1A1A] mb-1 md:mb-2">
                    平均単価(万円) <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="number" 
                    id="average_price"
                    name="average_price"
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-[#E0E0E0] rounded-lg md:rounded-xl focus:border-[#2C5F2D] focus:outline-none text-sm text-[#1A1A1A] bg-white"
                    placeholder="例：200"
                  />
                </div>

                <div>
                  <label htmlFor="profit_margin" className="block text-xs md:text-sm font-bold text-[#1A1A1A] mb-1 md:mb-2">
                    粗利率(%) <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="number" 
                    id="profit_margin"
                    name="profit_margin"
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-[#E0E0E0] rounded-lg md:rounded-xl focus:border-[#2C5F2D] focus:outline-none text-sm text-[#1A1A1A] bg-white"
                    placeholder="例：30"
                  />
                </div>

                <div>
                  <label htmlFor="inquiries" className="block text-xs md:text-sm font-bold text-[#1A1A1A] mb-1 md:mb-2">
                    月間問い合わせ数 <span className="text-red-500">*</span>
                  </label>
                  <select 
                    id="inquiries"
                    name="inquiries"
                    required
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-[#E0E0E0] rounded-lg md:rounded-xl focus:border-[#2C5F2D] focus:outline-none text-sm cursor-pointer text-[#1A1A1A] bg-white"
                  >
                    <option value="">選択してください</option>
                    <option value="0-3">0〜3件</option>
                    <option value="4-7">4〜7件</option>
                    <option value="8-15">8〜15件</option>
                    <option value="16+">16件以上</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs md:text-sm font-bold text-[#1A1A1A] mb-1 md:mb-2">
                  現在の課題・ご相談内容
                </label>
                <textarea 
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 md:px-4 py-2.5 md:py-3 border-2 border-[#E0E0E0] rounded-lg md:rounded-xl focus:border-[#2C5F2D] focus:outline-none text-sm resize-none text-[#1A1A1A]"
                  placeholder="例：相見積もりで値引きが多い、成約率が上がらない、など（500文字以内）"
                ></textarea>
                <p className="text-[10px] md:text-xs text-[#666666] mt-1">500文字以内でご入力ください</p>
              </div>
            </div>

            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#7CB342] text-[#2C5F2D] py-4 md:py-5 rounded-xl md:rounded-2xl font-black text-sm md:text-lg hover:bg-[#8BC34A] transition-colors mt-6 md:mt-8 flex items-center justify-center gap-2 md:gap-3 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isSubmitting ? '送信中...' : '粗利改善シミュレーションを申し込む'}
              {!isSubmitting && <i className="ri-arrow-right-line text-xl md:text-2xl"></i>}
            </button>

            <p className="text-[10px] md:text-xs text-[#666666] text-center mt-4 md:mt-6">
              ※3営業日以内に担当者よりご連絡いたします
            </p>
          </form>
        </div>
      </section>

      {/* フッター */}
      <footer className="bg-[#2A2A2A] text-white py-10 md:py-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <img src="https://static.readdy.ai/image/4b210e4d3d9fe9a73b886d10fa116566/0e64fe7d7ddf660e67742842ccecd709.png" alt="ロゴ" className="h-6 md:h-8 w-auto" />
              </div>
              <p className="text-xs md:text-sm text-[#999999] leading-relaxed">
                外構工事専門の<br />粗利改善コンサルティング
              </p>
            </div>

            <div>
              <h4 className="font-bold mb-3 md:mb-4 text-sm md:text-base">サービス</h4>
              <ul className="space-y-2 md:space-y-3 text-xs md:text-sm text-[#999999]">
                <li><a href="#contact" className="hover:text-white transition-colors cursor-pointer">無料診断</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">設計実装プラン</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">伴走改善プラン</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-3 md:mb-4 text-sm md:text-base">会社情報</h4>
              <ul className="space-y-2 md:space-y-3 text-xs md:text-sm text-[#999999]">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">会社概要</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">プライバシーポリシー</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">特定商取引法</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors cursor-pointer">お問い合わせ</a></li>
              </ul>
            </div>

            <div className="col-span-2 md:col-span-1">
              <h4 className="font-bold mb-3 md:mb-4 text-sm md:text-base">SNS</h4>
              <div className="flex gap-3 md:gap-4">
                <a href="#" className="w-9 h-9 md:w-10 md:h-10 bg-[#3A3A3A] rounded-full flex items-center justify-center hover:bg-[#2C5F2D] transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-base md:text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 md:w-10 md:h-10 bg-[#3A3A3A] rounded-full flex items-center justify-center hover:bg-[#2C5F2D] transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-base md:text-lg"></i>
                </a>
                <a href="#" className="w-9 h-9 md:w-10 md:h-10 bg-[#3A3A3A] rounded-full flex items-center justify-center hover:bg-[#2C5F2D] transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-base md:text-lg"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-[#3A3A3A] pt-6 md:pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs md:text-sm text-[#999999] text-center md:text-left">
              © 2025 外構工事専門コンサルティング. All rights reserved.
            </p>
            <div className="flex items-center gap-3 md:gap-4">
              <div className="bg-[#7CB342] text-[#2A2A2A] px-3 md:px-4 py-1 rounded-full text-[10px] md:text-xs font-bold">
                粗利改善専門
              </div>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[10px] md:text-xs text-[#999999] hover:text-white transition-colors">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
